import React, { useEffect, useRef, useState } from 'react';
import CryptoJS from 'crypto-js';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

const publishMailOAuthCompleted = ({ success, email = '', error = '', resumed = [] }) => {
  try {
    const payload = {
      provider: 'outlook',
      success: Boolean(success),
      email,
      error,
      resumed,
      createdAt: Date.now(),
      nonce: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    };
    localStorage.setItem('mail_oauth_completed', JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('mail_oauth_completed', { detail: payload }));
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('mail_oauth');
      channel.postMessage(payload);
      channel.close();
    }
  } catch (_) {
    // Best-effort fallback for the opener-less OAuth path.
  }
};

const getMailOAuthReturnUrl = () => {
  try {
    return (
      sessionStorage.getItem('mail_oauth_return_url') ||
      localStorage.getItem('mail_oauth_return_url') ||
      '/account/credentials'
    );
  } catch (_) {
    return '/account/credentials';
  }
};

const clearMailOAuthReturnUrl = () => {
  try { sessionStorage.removeItem('mail_oauth_return_url'); } catch (_) { /* ignore */ }
  try { localStorage.removeItem('mail_oauth_return_url'); } catch (_) { /* ignore */ }
};

const getMailOAuthResumeContext = () => {
  try {
    const raw = sessionStorage.getItem('mail_oauth_resume') || localStorage.getItem('mail_oauth_resume');
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
};

export default function OutlookCallbackPage() {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const hasExchangedRef = useRef(false);

  useEffect(() => {
    if (hasExchangedRef.current) return;
    hasExchangedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const authError = params.get('error');

    if (authError) {
      const msg = authError === 'access_denied'
        ? 'You did not grant the required Microsoft permissions.'
        : `Microsoft authorization failed: ${authError}`;
      setStatus('error');
      setError(msg);
      if (window.opener) {
        window.opener.postMessage({ type: 'OUTLOOK_CONNECTED', success: false, error: msg }, '*');
      }
      return;
    }

    if (!code) {
      setStatus('error');
      setError('No authorization code received from Microsoft.');
      return;
    }

    const callbackUrl = `${window.location.origin}/outlook-callback`;
    const resumeContext = getMailOAuthResumeContext();
    const payload = {
      code,
      state,
      callback_url: callbackUrl,
      pending_mail_auth_action_id: resumeContext.pendingMailAuthActionId || resumeContext.pending_mail_auth_action_id || undefined,
    };
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

    fetch(`${API_URL}/outlook/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: encrypted }),
    })
      .then(async (res) => {
        const responseData = await res.json();
        if (!res.ok) {
          throw new Error(responseData.error || 'Failed to connect Outlook. Please try again.');
        }

        let data = {};
        if (responseData.data && typeof responseData.data === 'string') {
          const bytes = CryptoJS.AES.decrypt(responseData.data, SECRET_KEY);
          data = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        }

        setEmail(data.outlook_email || 'Outlook');
        setStatus('success');
        publishMailOAuthCompleted({
          success: true,
          email: data.outlook_email || '',
          resumed: data.pending_mail_auth_resumed || [],
        });

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'OUTLOOK_CONNECTED',
              success: true,
              outlook_email: data.outlook_email,
              pending_mail_auth_resumed: data.pending_mail_auth_resumed || [],
            },
            '*'
          );
        }

        let count = 3;
        const timer = setInterval(() => {
          count -= 1;
          setCountdown(count);
          if (count <= 0) {
            clearInterval(timer);
            if (window.opener) {
              window.close();
            } else {
              const returnUrl = getMailOAuthReturnUrl();
              clearMailOAuthReturnUrl();
              window.location.replace(returnUrl);
            }
          }
        }, 1000);
      })
      .catch((err) => {
        setStatus('error');
        setError(err.message);
        publishMailOAuthCompleted({ success: false, error: err.message });
        if (window.opener) {
          window.opener.postMessage({ type: 'OUTLOOK_CONNECTED', success: false, error: err.message }, '*');
        }
      });
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{
          ...styles.icon,
          background: status === 'success' ? '#2563eb' : status === 'error' ? '#dc2626' : '#eef4ff',
          color: status === 'processing' ? '#2563eb' : '#fff',
        }}>
          {status === 'processing' ? 'O' : status === 'success' ? '✓' : '×'}
        </div>
        <h1 style={styles.title}>
          {status === 'processing' && 'Connecting Outlook'}
          {status === 'success' && 'Outlook Connected'}
          {status === 'error' && 'Connection Failed'}
        </h1>
        <p style={styles.subtitle}>
          {status === 'processing' && 'Setting up your Outlook integration.'}
          {status === 'success' && `Your Outlook account (${email}) is now connected.`}
          {status === 'error' && (error || 'Something went wrong. Please try again.')}
        </p>
        {status === 'success' && <p style={styles.close}>Closing in {countdown}...</p>}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    width: 'min(420px, calc(100vw - 32px))',
    background: '#fff',
    borderRadius: 16,
    padding: 32,
    textAlign: 'center',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.12)',
  },
  icon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    margin: '0 auto 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    fontWeight: 700,
  },
  title: {
    margin: 0,
    fontSize: 24,
    color: '#111827',
  },
  subtitle: {
    margin: '12px 0 0',
    fontSize: 14,
    lineHeight: 1.6,
    color: '#4b5563',
  },
  close: {
    margin: '18px 0 0',
    fontSize: 12,
    color: '#64748b',
  },
};
